import { Box, Collapse, Grid, Typography, IconButton, Fab, Badge, Tooltip, useTheme, Card } from '@mui/material'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { connect } from 'react-redux';
import { ButtonBasic } from '../../components'
import TicketsCalidadService from '../../services/ticketsCalidad.service';
import { v4 as uuidv4 } from 'uuid';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import PublicIcon from '@mui/icons-material/Public';
import FilterListIcon from '@mui/icons-material/FilterList';
import DashboardCards from './ticketsCalidad/DashboardCards';
import DashboardCharts from './ticketsCalidad/DashboardCharts';
import FiltersDialog from './ticketsCalidad/FiltersDialog';
import TicketsList from './ticketsCalidad/TicketsList';

const INITIAL_FILTERS = {
    estado: [],
    stage: [],
    prioridad: [],
    tipoTicketId: [],
    subTipoTicketId: [],
    motivoId: [],
    motivoBajaId: [],
    nivelAdmTkId: [],
    grupoId: [],
    vencimiento: [],
    fechaDesde: null,
    fechaHasta: null,
};

const TicketsCalidadScreen = ({ ticketrefresh }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';

    // Dashboard state
    const [dashboardGeneral, setDashboardGeneral] = useState(null);
    const [dashboardFiltrado, setDashboardFiltrado] = useState(null);
    const [isLoadingDashGeneral, setIsLoadingDashGeneral] = useState(false);
    const [isLoadingDashFiltrado, setIsLoadingDashFiltrado] = useState(false);
    const [dashboardTab, setDashboardTab] = useState('general');
    const [dashboardOpen, setDashboardOpen] = useState(true);

    // Tickets list state
    const [tickets, setTickets] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Lazy loading
    const [ticketsDisplayed, setTicketsDisplayed] = useState([]);
    const [displayCount, setDisplayCount] = useState(20);

    // Filters
    const [appliedFilters, setAppliedFilters] = useState({ ...INITIAL_FILTERS });
    const [pendingFilters, setPendingFilters] = useState({ ...INITIAL_FILTERS });
    const [openFilterDialog, setOpenFilterDialog] = useState(false);

    // Search
    const [textSearch, setTextSearch] = useState('');
    const searchTimeout = useRef(null);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (appliedFilters.estado?.length > 0) count++;
        if (appliedFilters.stage?.length > 0) count++;
        if (appliedFilters.prioridad?.length > 0) count++;
        if (appliedFilters.tipoTicketId?.length > 0) count++;
        if (appliedFilters.subTipoTicketId?.length > 0) count++;
        if (appliedFilters.motivoId?.length > 0) count++;
        if (appliedFilters.motivoBajaId?.length > 0) count++;
        if (appliedFilters.nivelAdmTkId?.length > 0) count++;
        if (appliedFilters.grupoId?.length > 0) count++;
        if (appliedFilters.vencimiento?.length > 0) count++;
        if (appliedFilters.fechaDesde || appliedFilters.fechaHasta) count++;
        return count;
    }, [appliedFilters]);

    const buildQueryParams = useCallback(() => {
        const params = { page: currentPage, limit: 20 };
        const f = appliedFilters;
        if (f.estado?.length > 0) params.estado = f.estado.join(',');
        if (f.stage?.length > 0) params.stage = f.stage.join(',');
        if (f.prioridad?.length > 0) params.prioridad = f.prioridad.join(',');
        if (f.tipoTicketId?.length > 0) params.tipoTicketId = f.tipoTicketId.join(',');
        if (f.subTipoTicketId?.length > 0) params.subTipoTicketId = f.subTipoTicketId.join(',');
        if (f.motivoId?.length > 0) params.motivoId = f.motivoId.join(',');
        if (f.motivoBajaId?.length > 0) params.motivoBajaId = f.motivoBajaId.join(',');
        if (f.nivelAdmTkId?.length > 0) params.nivelAdmTkId = f.nivelAdmTkId.join(',');
        if (f.grupoId?.length > 0) params.grupoId = f.grupoId.join(',');
        if (f.vencimiento?.length > 0) params.vencimiento = f.vencimiento.join(',');
        if (f.fechaDesde) params.fechaDesde = f.fechaDesde;
        if (f.fechaHasta) params.fechaHasta = f.fechaHasta;
        if (textSearch?.trim()) params.search = textSearch.trim();
        return params;
    }, [currentPage, appliedFilters, textSearch]);

    const fetchTickets = useCallback(async (pageOverride) => {
        setIsLoading(true);
        try {
            const params = buildQueryParams();
            if (pageOverride !== undefined) params.page = pageOverride;
            const resp = await TicketsCalidadService.getTickets(params);
            const data = resp?.data || resp;
            const parsed = (data?.content || []).map((tk) => ({
                ticket: tk,
                clientes: tk?.clientes || [],
                id_uxt: uuidv4(),
                nombreCompleto: `#${tk?.id_tk} - ${tk?.titulo_tk}`
            }));
            if ((pageOverride || params.page) > 0) {
                setTickets(prev => {
                    const updated = [...prev, ...parsed];
                    setDisplayCount(updated.length);
                    return updated;
                });
            } else {
                setTickets(parsed);
                setDisplayCount(20);
            }
            setTotalElements(data?.totalElements || 0);
            setTotalPages(data?.totalPages || 0);
            setCurrentPage(data?.currentPage ?? 0);
        } catch (err) {
            console.error('Error fetching tickets calidad:', err);
        } finally {
            setIsLoading(false);
            setIsInitialLoad(false);
        }
    }, [buildQueryParams]);

    const fetchDashboardGeneral = useCallback(async () => {
        setIsLoadingDashGeneral(true);
        try {
            const resp = await TicketsCalidadService.getDashboard({});
            setDashboardGeneral(resp?.data || resp);
        } catch (err) {
            console.error('Error fetching dashboard general:', err);
        } finally {
            setIsLoadingDashGeneral(false);
        }
    }, []);

    const fetchDashboardFiltrado = useCallback(async () => {
        setIsLoadingDashFiltrado(true);
        try {
            const params = {};
            const f = appliedFilters;
            if (f.estado?.length > 0) params.estado = f.estado.join(',');
            if (f.stage?.length > 0) params.stage = f.stage.join(',');
            if (f.prioridad?.length > 0) params.prioridad = f.prioridad.join(',');
            if (f.tipoTicketId?.length > 0) params.tipoTicketId = f.tipoTicketId.join(',');
            if (f.subTipoTicketId?.length > 0) params.subTipoTicketId = f.subTipoTicketId.join(',');
            if (f.motivoId?.length > 0) params.motivoId = f.motivoId.join(',');
            if (f.motivoBajaId?.length > 0) params.motivoBajaId = f.motivoBajaId.join(',');
            if (f.nivelAdmTkId?.length > 0) params.nivelAdmTkId = f.nivelAdmTkId.join(',');
            if (f.grupoId?.length > 0) params.grupoId = f.grupoId.join(',');
            if (f.vencimiento?.length > 0) params.vencimiento = f.vencimiento.join(',');
            if (f.fechaDesde) params.fechaDesde = f.fechaDesde;
            if (f.fechaHasta) params.fechaHasta = f.fechaHasta;
            const resp = await TicketsCalidadService.getDashboard(params);
            setDashboardFiltrado(resp?.data || resp);
        } catch (err) {
            console.error('Error fetching dashboard filtrado:', err);
        } finally {
            setIsLoadingDashFiltrado(false);
        }
    }, [appliedFilters]);

    // Initial load
    useEffect(() => {
        fetchTickets(0);
        fetchDashboardGeneral();
        fetchDashboardFiltrado();
    }, []);

    // Re-fetch when applied filters change
    useEffect(() => {
        if (!isInitialLoad) {
            setCurrentPage(0);
            fetchTickets(0);
            fetchDashboardFiltrado();
        }
    }, [appliedFilters]);

    // Debounced text search
    useEffect(() => {
        if (!isInitialLoad) {
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = setTimeout(() => {
                setCurrentPage(0);
                fetchTickets(0);
            }, 500);
            return () => clearTimeout(searchTimeout.current);
        }
    }, [textSearch]);

    // Refresh from redux
    useEffect(() => {
        if (ticketrefresh?.reloadingInd || ticketrefresh?.reloadingGroup) {
            fetchTickets(0);
            fetchDashboardGeneral();
            fetchDashboardFiltrado();
        }
    }, [ticketrefresh]);

    useEffect(() => {
        setTicketsDisplayed(tickets.slice(0, displayCount));
    }, [tickets, displayCount]);

    useEffect(() => {
        document.title = 'Tickets Calidad';
    }, []);

    // Filter handlers
    const onPendingDateChange = (dates) => {
        const [start, end] = dates;
        setPendingFilters(prev => ({
            ...prev,
            fechaDesde: start ? start.toISOString().split('T')[0] : null,
            fechaHasta: end ? end.toISOString().split('T')[0] : null,
        }));
    };

    const onOpenFilters = () => {
        setPendingFilters({ ...appliedFilters });
        setOpenFilterDialog(true);
    };

    const onCloseFilters = () => setOpenFilterDialog(false);

    const onApplyFilters = () => {
        setAppliedFilters({ ...pendingFilters });
        setOpenFilterDialog(false);
    };

    const onClearFilters = () => setPendingFilters({ ...INITIAL_FILTERS });

    const onRefresh = () => {
        setCurrentPage(0);
        fetchTickets(0);
        fetchDashboardGeneral();
        fetchDashboardFiltrado();
    };

    const styleInput = useMemo(() => ({
        '& .MuiInputLabel-root': {
            color: isDarkMode ? '#b0bec5' : '#7f8c8d',
            fontSize: 14,
            marginTop: -0.4
        },
        '& fieldset': {
            borderRadius: 2,
            height: 50,
            border: 1,
            borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0',
        },
        '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
                border: 1,
                color: isDarkMode ? '#64B5F6' : '#0364ff',
                height: 50,
            },
            '&:hover': {
                border: 0.1,
                borderRadius: 2,
                borderColor: isDarkMode ? '#64B5F6' : '#0364ff',
                height: 45,
            },
            '&.Mui-focused': {
                '&:hover': {
                    border: 0,
                    borderRadius: 2,
                    height: 45,
                }
            },
            color: isDarkMode ? '#e0e0e0' : '#2c3e50',
            height: 45,
            width: { xs: '100%', sm: 350, md: 450 }
        }
    }), [isDarkMode]);

    const dashboard = dashboardTab === 'general' ? dashboardGeneral : dashboardFiltrado;
    const isLoadingDash = dashboardTab === 'general' ? isLoadingDashGeneral : isLoadingDashFiltrado;

    return (
        <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }} className='side'>
            <Grid container p={3} spacing={2}>
                {/* Header */}
                <Grid item xs={12} md={6}>
                    <Box sx={{
                        animation: 'fadeInDown 0.6s ease-out',
                        '@keyframes fadeInDown': {
                            '0%': { opacity: 0, transform: 'translateY(-20px)' },
                            '100%': { opacity: 1, transform: 'translateY(0)' }
                        }
                    }}>
                        <Typography
                            variant="h2"
                            sx={{
                                fontWeight: 'bold',
                                background: isDarkMode
                                    ? 'linear-gradient(135deg, #64B5F6 0%, #42A5F5 100%)'
                                    : 'linear-gradient(135deg, #2c3e50 0%, #0364ff 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                mb: 1
                            }}
                        >
                            Tickets Calidad
                        </Typography>
                        <Box sx={{
                            width: '60px',
                            height: '4px',
                            background: 'linear-gradient(90deg, #0364ff 0%, #4a90e2 100%)',
                            borderRadius: '10px',
                            animation: 'expandWidth 0.8s ease-out',
                            '@keyframes expandWidth': {
                                '0%': { width: '0px' },
                                '100%': { width: '60px' }
                            }
                        }} />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: { xs: 'center', sm: 'flex-end' },
                        animation: 'fadeIn 0.8s ease-out',
                        '@keyframes fadeIn': { '0%': { opacity: 0 }, '100%': { opacity: 1 } }
                    }}>
                        <ButtonBasic
                            iconStart={<RefreshIcon sx={{ color: '#fff' }} />}
                            textButton="Actualizar"
                            sx={{
                                width: { xs: '100%', sm: 180 },
                                height: 42,
                                color: '#fff',
                                borderRadius: '12px',
                                fontWeight: 600,
                                fontSize: 14,
                                textTransform: 'none',
                                boxShadow: '0 4px 14px rgba(3, 100, 255, 0.3)',
                                transition: 'all 0.3s ease',
                            }}
                            onClick={onRefresh}
                            variant='wk'
                            color='nextStep'
                        />
                    </Box>
                </Grid>

                {/* Dashboard */}
                <Grid item xs={12}>
                    <Card sx={{p: 1, borderRadius: 2}}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 2,
                            flexWrap: 'wrap',
                            gap: 1.5,
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '10px',
                                    background: 'linear-gradient(135deg, #0364ff 0%, #4a90e2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 3px 10px rgba(3, 100, 255, 0.3)',
                                }}>
                                    <AssessmentIcon sx={{ color: '#fff', fontSize: 20 }} />
                                </Box>
                                <Typography sx={{
                                    color: isDarkMode ? '#e0e0e0' : '#2c3e50',
                                    fontWeight: 700,
                                    fontSize: 16,
                                }}>
                                    Dashboard
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => setDashboardOpen(prev => !prev)}
                                    sx={{
                                        color: isDarkMode ? '#b0bec5' : '#7f8c8d',
                                        transition: 'transform 0.3s ease',
                                        transform: dashboardOpen ? 'rotate(0deg)' : 'rotate(180deg)',
                                        '&:hover': { background: isDarkMode ? '#3d4a62' : '#f0f0f0' }
                                    }}
                                >
                                    <ExpandLessIcon />
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Box
                                    onClick={() => setDashboardTab('general')}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.8,
                                        px: 2,
                                        py: 0.8,
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: 13,
                                        transition: 'all 0.25s ease',
                                        border: dashboardTab === 'general'
                                            ? '1.5px solid #0364ff'
                                            : isDarkMode ? '1.5px solid #3d4a62' : '1.5px solid #e0e0e0',
                                        background: dashboardTab === 'general'
                                            ? isDarkMode ? 'rgba(3, 100, 255, 0.15)' : 'rgba(3, 100, 255, 0.08)'
                                            : 'transparent',
                                        color: dashboardTab === 'general'
                                            ? '#0364ff'
                                            : isDarkMode ? '#b0bec5' : '#7f8c8d',
                                        '&:hover': {
                                            background: dashboardTab === 'general'
                                                ? isDarkMode ? 'rgba(3, 100, 255, 0.2)' : 'rgba(3, 100, 255, 0.12)'
                                                : isDarkMode ? '#3d4a62' : '#f0f0f0',
                                        }
                                    }}
                                >
                                    <PublicIcon sx={{ fontSize: 18 }} />
                                    Datos Generales
                                </Box>
                                <Box
                                    onClick={() => setDashboardTab('filtrado')}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.8,
                                        px: 2,
                                        py: 0.8,
                                        borderRadius: '10px',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        fontSize: 13,
                                        transition: 'all 0.25s ease',
                                        border: dashboardTab === 'filtrado'
                                            ? '1.5px solid #9c27b0'
                                            : isDarkMode ? '1.5px solid #3d4a62' : '1.5px solid #e0e0e0',
                                        background: dashboardTab === 'filtrado'
                                            ? isDarkMode ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.08)'
                                            : 'transparent',
                                        color: dashboardTab === 'filtrado'
                                            ? '#9c27b0'
                                            : isDarkMode ? '#b0bec5' : '#7f8c8d',
                                        '&:hover': {
                                            background: dashboardTab === 'filtrado'
                                                ? isDarkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.12)'
                                                : isDarkMode ? '#3d4a62' : '#f0f0f0',
                                        }
                                    }}
                                >
                                    <FilterAltIcon sx={{ fontSize: 18 }} />
                                    Datos Filtrados
                                    {activeFilterCount > 0 && (
                                        <Box sx={{
                                            ml: 0.5,
                                            px: 0.8,
                                            py: 0.1,
                                            borderRadius: '6px',
                                            background: '#9c27b0',
                                            color: '#fff',
                                            fontSize: 11,
                                            fontWeight: 700,
                                            lineHeight: '16px',
                                            minWidth: 18,
                                            textAlign: 'center',
                                        }}>
                                            {activeFilterCount}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        <Collapse in={dashboardOpen} timeout={400}>
                            <DashboardCards
                                dashboard={dashboard}
                                isLoadingDash={isLoadingDash}
                                isDarkMode={isDarkMode}
                            />
                            <DashboardCharts
                                dashboard={dashboard}
                                isDarkMode={isDarkMode}
                            />
                        </Collapse>
                    </Card>
                </Grid>

                {/* Filters Dialog */}
                <FiltersDialog
                    open={openFilterDialog}
                    onClose={onCloseFilters}
                    onApply={onApplyFilters}
                    onClear={onClearFilters}
                    pendingFilters={pendingFilters}
                    setPendingFilters={setPendingFilters}
                    onPendingDateChange={onPendingDateChange}
                    isDarkMode={isDarkMode}
                />

                {/* Tickets List */}
                <TicketsList
                    tickets={tickets}
                    ticketsDisplayed={ticketsDisplayed}
                    totalElements={totalElements}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    isLoading={isLoading}
                    isInitialLoad={isInitialLoad}
                    displayCount={displayCount}
                    setDisplayCount={setDisplayCount}
                    fetchTickets={fetchTickets}
                    textSearch={textSearch}
                    setTextSearch={setTextSearch}
                    styleInput={styleInput}
                    isDarkMode={isDarkMode}
                />
            </Grid>

            {/* FAB Filtros */}
            <Tooltip title={`Filtros${activeFilterCount > 0 ? ` (${activeFilterCount} activos)` : ''}`} placement="left">
                <Fab
                    onClick={onOpenFilters}
                    sx={{
                        position: 'fixed',
                        bottom: 32,
                        right: 32,
                        width: 60,
                        height: 60,
                        background: 'linear-gradient(135deg, #0364ff 0%, #4a90e2 100%)',
                        color: '#fff',
                        boxShadow: '0 6px 20px rgba(3, 100, 255, 0.4)',
                        transition: 'all 0.3s ease',
                        zIndex: 1200,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #0250d0 0%, #3a7cd0 100%)',
                            transform: 'scale(1.08)',
                            boxShadow: '0 8px 28px rgba(3, 100, 255, 0.5)',
                        },
                    }}
                >
                    <Badge
                        badgeContent={activeFilterCount}
                        color="error"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: 11,
                                fontWeight: 700,
                                minWidth: 20,
                                height: 20,
                                borderRadius: '10px',
                                top: -4,
                                right: -4,
                            }
                        }}
                    >
                        <FilterListIcon sx={{ fontSize: 28 }} />
                    </Badge>
                </Fab>
            </Tooltip>
        </Box>
    );
};

const mapStateToProps = (state) => ({
    ticketrefresh: state.ticketrefresh
});

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(TicketsCalidadScreen);
